import { Box, CircularProgress, Stack } from "@mui/material";
import DownloadInvoicesButtonOrMenuItem from "atoms/DownloadInvoicesButtonOrMenuItem";
import SendInvoicesToAccountingButtonOrMenuItem from "atoms/SendInvoicesToAccountingButtonOrMenuItem";
import * as customHooks from "misc/customHooks";
import { InvoiceOverviewTable, SendInvoicesDialogButtonOrMenuItem } from "organisms";
import { GenerateDocumentProgressType } from "organisms/GenerateDocumentDialogButton/GenerateDocumentDialogButton.types";
import GenerateDocumentProgressBar from "organisms/GenerateDocumentDialogButton/GenerateDocumentProgressBar";
import { BookingRecordType, InvoiceRecordType } from "pages/Booking/Booking.types";
import { GeneratedDocumentRecordType } from "pages/DocumentTemplate/DocumentTemplate.types";
import { EventRecordType } from "pages/Event/Event.types";
import { FailedInvoiceResponse, InvoiceResponse } from "pages/Invoice/Invoice.types";
import { AttendeeRecordType } from "pages/SharedRecords.types";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useDeleteMany, useTranslate } from "react-admin";

type CreateInvoiceOverviewPropsType = {
  event?: EventRecordType;
  bookingsWithAttendees: BookingRecordType<AttendeeRecordType>[];
  invoiceResponses: InvoiceResponse[];
  showExternalAccountingFeature: boolean;
  documentTaskProgress: GenerateDocumentProgressType;
  generatedDocuments: GeneratedDocumentRecordType[];
};

function isSuccessfulInvoiceResponse(response: InvoiceResponse): response is InvoiceRecordType {
  return Object.hasOwn(response, "id");
}

export default function CreateInvoiceOverview(props: CreateInvoiceOverviewPropsType) {
  const {
    event,
    invoiceResponses,
    bookingsWithAttendees,
    showExternalAccountingFeature,
    documentTaskProgress,
    generatedDocuments,
  } = props;

  const [deleteMany] = useDeleteMany<InvoiceRecordType>();

  const [successfulInvoices, setSuccessfulInvoices] = useState<InvoiceRecordType[]>([]);
  const [failedResponsesWithBooking, setFailedResponsesWithBooking] = useState<
    { invoiceResponse: FailedInvoiceResponse; booking: BookingRecordType<AttendeeRecordType> }[]
  >([]);

  const { refetchHistory: refetchBookingHistory } = customHooks.useRecordHistoryContext();
  const { refetchHistory: refetchEventHistory } = customHooks.useParentRecordHistoryContext();
  const translate = useTranslate();

  const successfulInvoiceIds = useMemo(() => successfulInvoices.map((i) => i.id), [successfulInvoices]);

  const refreshHistories = () => {
    refetchBookingHistory();
    refetchEventHistory();
  };

  const documentGenerationFinished =
    !documentTaskProgress ||
    (!documentTaskProgress.error &&
      documentTaskProgress.current > 0 &&
      documentTaskProgress.current >= documentTaskProgress.total);

  useEffect(() => {
    const successful: InvoiceRecordType[] = [];
    const failed: { invoiceResponse: FailedInvoiceResponse; booking: BookingRecordType<AttendeeRecordType> }[] = [];

    for (let i = 0; i < invoiceResponses.length; i++) {
      const invoiceResponse = invoiceResponses[i];
      const booking = bookingsWithAttendees[i];

      if (isSuccessfulInvoiceResponse(invoiceResponse)) {
        successful.push(invoiceResponse);
      } else {
        failed.push({ invoiceResponse, booking });
      }
    }

    setSuccessfulInvoices(successful);
    setFailedResponsesWithBooking(failed);
  }, [invoiceResponses, bookingsWithAttendees]);

  // Handle deletion of invoices on task error
  useEffect(() => {
    if (documentTaskProgress?.error && successfulInvoices.length > 0) {
      deleteMany("invoices", { ids: successfulInvoiceIds });

      setSuccessfulInvoices([]);
      setFailedResponsesWithBooking(
        bookingsWithAttendees.map((booking) => ({
          booking,
          invoiceResponse: { message: [translate("invoice.error")] },
        })),
      );
    }
  }, [documentTaskProgress?.error]);

  if (invoiceResponses.length === 0) {
    return (
      <Box display="flex" justifyContent="center" m={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fragment>
      <Stack gap={2}>
        <GenerateDocumentProgressBar progress={documentTaskProgress} />

        <InvoiceOverviewTable
          showExternalAccountingFeature={showExternalAccountingFeature}
          invoices={successfulInvoices}
          failedInvoiceResponsesWithBookings={failedResponsesWithBooking}
          generatedDocuments={generatedDocuments}
        />
      </Stack>

      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={4} gap={1}>
        {showExternalAccountingFeature && (
          <SendInvoicesToAccountingButtonOrMenuItem
            disabled={!documentGenerationFinished}
            invoiceIds={successfulInvoiceIds}
          />
        )}

        <SendInvoicesDialogButtonOrMenuItem
          event={event}
          disabled={!documentGenerationFinished}
          invoices={successfulInvoices}
          onSuccess={refreshHistories}
          generatedDocuments={generatedDocuments}
        />

        <DownloadInvoicesButtonOrMenuItem ids={successfulInvoiceIds} disabled={!documentGenerationFinished} />
      </Box>
    </Fragment>
  );
}
